import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import MondayVibeTextIcon from './icons/MondayVibeTextIcon';
import { useMondayContext } from '@/context/MondayContextProvider';
import { persistWatermarkDismissed, readWatermarkDismissed } from '@/utils/vibe-watermark-storage';
import {
  WATERMARK_ARIA_LABEL_VIEWER,
  WATERMARK_EDIT_TEXT,
  WATERMARK_EVENTS,
  WATERMARK_LINK_KIND,
  WATERMARK_LOGO_URL,
  WATERMARK_MONDAY_BASE_URL,
  WATERMARK_TRACKING,
} from './vibe-watermark.constants';
import styles from './vibe-watermark.module.css';

declare const window: Window & {
  BigBrain?: (method: string, event: string, data: Record<string, unknown>) => void;
};

export function VibeWatermark() {
  const { context } = useMondayContext();
  const isCreator = context?.isCreator === true;
  const aiAppId = typeof context?.aiAppId === 'string' ? context.aiAppId : undefined;
  const accountSlug = typeof context?.accountSlug === 'string' ? context.accountSlug : undefined;
  const accountBaseUrl = typeof context?.accountBaseUrl === 'string' ? context.accountBaseUrl : undefined;
  const [isVisible, setIsVisible] = useState(() => !readWatermarkDismissed(aiAppId));

  useEffect(() => {
    if (readWatermarkDismissed(aiAppId)) {
      setIsVisible(false);
    }
  }, [aiAppId]);

  if (context?.hideWatermark === true) {
    return null;
  }

  const linkUrl =
    isCreator && aiAppId && accountBaseUrl
      ? `${accountBaseUrl}/vibe/app/${aiAppId}?kind=${WATERMARK_LINK_KIND}`
      : `${WATERMARK_MONDAY_BASE_URL}/vibe?kind=${WATERMARK_LINK_KIND}`;

  const viewerType = isCreator ? WATERMARK_TRACKING.VIEWER_TYPE_CREATOR : WATERMARK_TRACKING.VIEWER_TYPE_VIEWER;

  const handleClick = () => {
    window.BigBrain?.('track', WATERMARK_EVENTS.CLICKED, {
      info1: WATERMARK_TRACKING.INFO1,
      app_id: aiAppId ?? 0,
      account_slug: accountSlug,
      viewer_type: viewerType,
      placement: isCreator ? WATERMARK_TRACKING.PLACEMENT_EDITOR : WATERMARK_TRACKING.PLACEMENT_LANDING,
    });
    window.open(linkUrl, '_blank', 'noopener,noreferrer');
  };

  const handleClose = (e: React.MouseEvent) => {
    window.BigBrain?.('track', WATERMARK_EVENTS.CLOSED, {
      info1: WATERMARK_TRACKING.INFO1,
      app_id: aiAppId ?? 0,
      account_slug: accountSlug,
      viewer_type: viewerType,
    });
    e.preventDefault();
    e.stopPropagation();
    setIsVisible(false);
    persistWatermarkDismissed(aiAppId);
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div className={styles.watermark}>
      <button onClick={handleClick} className={styles.content} aria-label={isCreator ? WATERMARK_EDIT_TEXT : WATERMARK_ARIA_LABEL_VIEWER}>
        <img src={WATERMARK_LOGO_URL} alt="Vibe" className={styles.logo} />
        {isCreator ? (
          <span className={styles.text}>{WATERMARK_EDIT_TEXT}</span>
        ) : (
          <MondayVibeTextIcon className={styles.mondayVibeText} />
        )}
      </button>
      <button onClick={handleClose} className={styles.closeButton} aria-label="Dismiss">
        <X size={16} />
      </button>
    </div>
  );
}
